import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Drawer } from 'antd'
import Select from 'react-select'
import { useSnackbar } from 'notistack'
import { Link, useHistory } from 'react-router-dom'

import Receive from '../../components/reusable/receive'
import { notifOps, server, stringifyDate, useListData, usePet } from '../../modules'

const queueOptions = [
    { label: 'All preauthorizations', value: '' },
    { label: 'Awaiting doctor approval', value: 'awaiting-doctor-approval' },
    { label: 'Awaiting visit date', value: 'awaiting-visit-date' },
    { label: 'Due today', value: 'due-today' },
    { label: 'Overdue', value: 'overdue' },
]

const statusOptions = [
    { label: 'All statuses', value: '' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Expired', value: 'expired' },
]

const categoryOptions = [
    { label: 'All categories', value: '' },
    { label: 'Surgical', value: 'SURGICAL' },
    { label: 'Imaging', value: 'IMAGING' },
    { label: 'Renal', value: 'RENAL' },
    { label: 'Oncology', value: 'ONCOLOGY' },
    { label: 'Optical', value: 'OPTICAL' },
]

const badgeStyles = {
    approved: 'bg-green-100 text-green-700 border-green-200',
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
    expired: 'bg-gray-100 text-gray-700 border-gray-200',
}

const queueSummaryStyles = {
    'awaiting-doctor-approval': 'border-yellow-200 bg-yellow-50 text-yellow-900',
    'awaiting-visit-date': 'border-blue-200 bg-blue-50 text-blue-900',
    'due-today': 'border-indigo-200 bg-indigo-50 text-indigo-900',
    overdue: 'border-red-200 bg-red-50 text-red-900',
}

const ShaPreauths = () => {
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar()
    const [string, setString] = useState('')
    const [referenceNumber, setReferenceNumber] = useState('')
    const [selectedQueue, setSelectedQueue] = useState(queueOptions[0])
    const [selectedStatus, setSelectedStatus] = useState(statusOptions[0])
    const [selectedCategory, setSelectedCategory] = useState(categoryOptions[0])
    const [selectedPreauth, setSelectedPreauth] = useState(null)
    const [detailsVisible, setDetailsVisible] = useState(false)
    const [summaryPreauths, setSummaryPreauths] = useState([])
    const [actioningPreauth, setActioningPreauth] = useState(null)

    const [
        { loading, error, fetch },
        { list: preauths = [], count = 0 },
        { page = 1, limit = 10, navigate, Paginate },
    ] = useListData('searchPreauths', {
        string,
        referenceNumber,
        queue: selectedQueue?.value || undefined,
        status: selectedStatus?.value || undefined,
        category: selectedCategory?.value || undefined,
        limit: 50,
    }, { notify: true, manual: true }, 'preauths')

    const [{ loading: detailLoading }, fetchPreauthStatus] = usePet('preauthStatus', {
        id: selectedPreauth?._id,
    }, {
        notify: false,
        manual: true,
        callback: ({ data }) => setSelectedPreauth(data),
    })

    const [, fetchSummaryPreauths] = usePet('searchPreauths', {
        string,
        referenceNumber,
        status: selectedStatus?.value || undefined,
        category: selectedCategory?.value || undefined,
        limit: 250,
        page: 1,
    }, {
        notify: false,
        manual: true,
        callback: ({ data }) => setSummaryPreauths(data?.preauths ?? []),
    })

    const [{ loading: refreshing }, refreshPreauthStatus] = usePet('refreshPreauthStatus', {
        id: selectedPreauth?._id,
    }, {
        notify: true,
        manual: true,
        callback: ({ data }) => {
            setSelectedPreauth(data)
            fetch()
        },
        load: { msg: 'Refreshing SHA preauthorization status...' }
    })

    const [{ loading: resending }, resendPreauthDoctorConsent] = usePet('resendPreauthDoctorConsent', {
        id: selectedPreauth?._id,
    }, {
        notify: true,
        manual: true,
        callback: ({ data }) => {
            setSelectedPreauth(data)
            fetch()
        },
        load: { msg: 'Requesting SHA doctor consent...' }
    })

    useEffect(() => {
        fetch()
    }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        navigate(1)
        fetch()
    }, [string, referenceNumber, selectedQueue?.value, selectedStatus?.value, selectedCategory?.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchSummaryPreauths()
    }, [string, referenceNumber, selectedStatus?.value, selectedCategory?.value]) // eslint-disable-line react-hooks/exhaustive-deps

    const today = new Date()
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    const authHeaders = useMemo(() => {
        const token = sessionStorage.getItem('token')
        return token ? { Authorization: `Bearer ${token}` } : {}
    }, [])

    const visitDateLabel = preauth => {
        if (!preauth?.scheduledVisitDate) return 'No visit date'
        const visitDate = new Date(preauth.scheduledVisitDate)
        if (Number.isNaN(visitDate.getTime())) return 'No visit date'
        if (preauth?.consumedAt) return `Consumed ${stringifyDate(preauth.consumedAt)}`
        if (visitDate < startOfToday) return `Overdue since ${stringifyDate(visitDate)}`
        if (visitDate.toDateString() === startOfToday.toDateString()) return `Due today • ${stringifyDate(visitDate)}`
        return `Scheduled ${stringifyDate(visitDate)}`
    }

    const resolveQueueKey = preauth => {
        if (!preauth?.isElective) return ''
        if (preauth?.consumedAt) return 'consumed'
        if (preauth?.shaStatus === 'PENDING_DOCTOR_APPROVAL') return 'awaiting-doctor-approval'
        if (preauth?.shaStatus !== 'FINALISED') return ''
        if (!preauth?.scheduledVisitDate) return 'awaiting-visit-date'

        const visitDate = new Date(preauth.scheduledVisitDate)
        if (Number.isNaN(visitDate.getTime())) return 'awaiting-visit-date'
        if (visitDate < startOfToday) return 'overdue'
        if (visitDate.toDateString() === startOfToday.toDateString()) return 'due-today'
        return 'awaiting-visit-date'
    }

    const queueSummary = queueOptions
        .filter(option => option.value)
        .map(option => ({
            ...option,
            count: summaryPreauths.filter(preauth => resolveQueueKey(preauth) === option.value).length,
        }))

    const clearFilters = () => {
        setString('')
        setReferenceNumber('')
        setSelectedQueue(queueOptions[0])
        setSelectedStatus(statusOptions[0])
        setSelectedCategory(categoryOptions[0])
    }

    const activeFilters = [
        selectedQueue?.value ? `Queue: ${selectedQueue.label}` : null,
        selectedStatus?.value ? `Status: ${selectedStatus.label}` : null,
        selectedCategory?.value ? `Category: ${selectedCategory.label}` : null,
        string?.trim() ? `Search: ${string.trim()}` : null,
        referenceNumber?.trim() ? `Ref: ${referenceNumber.trim()}` : null,
    ].filter(Boolean)

    const openPreauth = preauth => {
        setSelectedPreauth(preauth)
        setDetailsVisible(true)
        fetchPreauthStatus({ id: preauth?._id })
    }

    const canActionVisit = preauth => {
        if (!preauth?.patient) return false
        if (preauth?.consumedAt) return false
        if (preauth?.shaStatus !== 'FINALISED') return false
        if (preauth?.accessPoint === 'IP') return false
        if (preauth?.isElective) {
            const queueKey = resolveQueueKey(preauth)
            return queueKey === 'due-today' || queueKey === 'overdue'
        }
        return true
    }

    const actionVisitLabel = preauth => {
        const queueKey = resolveQueueKey(preauth)
        if (queueKey === 'due-today' || queueKey === 'overdue') return 'Action Visit'
        if (preauth?.isElective) return 'Open Visit Context'
        return 'Open Billing'
    }

    const openPatientBillingContext = preauth => {
        history.push(`/app/accounts/sales/patients/${preauth.patient}?preauth=${preauth._id}&tab=bills`)
    }

    const actionPreauthVisit = async preauth => {
        if (!canActionVisit(preauth) || actioningPreauth === preauth?._id) return

        try {
            setActioningPreauth(preauth._id)

            const visitResp = await axios({
                url: `${server}/api/patientActiveVisit`,
                method: 'post',
                headers: authHeaders,
                data: { patient: preauth.patient, timeStamp: new Date() },
            })

            if (visitResp?.data?.data?._id) {
                enqueueSnackbar('Opened patient billing context for the active visit', notifOps('success'))
                openPatientBillingContext(preauth)
                return
            }

            await axios({
                url: `${server}/api/ensurePatientVisit`,
                method: 'post',
                headers: authHeaders,
                data: { patient: preauth.patient, timeStamp: new Date() },
            })

            enqueueSnackbar('Visit prepared. Complete patient consent/check-in before routing, then continue from billing context.', notifOps('success'))
            fetch()
            setTimeout(() => openPatientBillingContext(preauth), 0)
        } catch (err) {
            const message = err?.response?.data?.message ?? err?.message ?? 'Unable to action this SHA preauthorization visit'
            enqueueSnackbar(message, notifOps('warning'))
        } finally {
            setActioningPreauth(null)
        }
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mx-auto">
            {
                detailsVisible &&
                <Drawer
                    visible={ detailsVisible }
                    width="42%"
                    onClose={ () => {
                        setDetailsVisible(false)
                        setSelectedPreauth(null)
                    } }
                    closable
                    maskClosable
                    title={ selectedPreauth?.procedureName ?? 'SHA preauthorization' }>
                    <Receive loading={ detailLoading }>
                        {
                            selectedPreauth &&
                            <div className="space-y-4">
                                <div className="rounded-sm border border-gray-200 bg-gray-50 px-4 py-3">
                                    <div className="mb-3 rounded-sm border border-indigo-200 bg-indigo-50 px-3 py-3">
                                        <p className="text-xs font-semibold text-indigo-800 uppercase mb-1">Operational use</p>
                                        <p className="text-sm text-indigo-900 mb-1">
                                            Use this workbench for scheduled, elective, delayed, or monitored SHA preauthorizations.
                                        </p>
                                        <p className="text-xs text-indigo-800 mb-0">
                                            The inline billing interruption modal is for same-visit action. This workbench is the place to monitor approval progress, resend doctor consent, and action due visits.
                                        </p>
                                    </div>
                                    <div className="grid gap-3 md:grid-cols-2 sm:grid-cols-1">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Patient</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.patientData?.name ?? '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Reference Number</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.referenceNumber ?? '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Category</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.category ?? '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">SHA Status</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.shaStatus ?? '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Estimated Amount</p>
                                            <p className="text-sm font-medium mb-0">Ksh. {selectedPreauth?.estimatedAmount ?? 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Approved Amount</p>
                                            <p className="text-sm font-medium mb-0">Ksh. {selectedPreauth?.approvedAmount ?? 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Doctor</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.doctorData?.name ?? '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Elective</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.isElective ? 'Yes' : 'No'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Visit Date</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.scheduledVisitDate ? stringifyDate(selectedPreauth.scheduledVisitDate) : '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Visit Queue</p>
                                            <p className="text-sm font-medium mb-0">{visitDateLabel(selectedPreauth)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Access Point</p>
                                            <p className="text-sm font-medium mb-0">{selectedPreauth?.accessPoint ?? 'OP'}</p>
                                        </div>
                                    </div>
                                </div>

                                {
                                    selectedPreauth?.clinicalIndication &&
                                    <div className="rounded-sm border border-blue-200 bg-blue-50 px-4 py-3">
                                        <p className="text-xs font-semibold text-blue-800 mb-1">Clinical Indication</p>
                                        <p className="text-sm text-blue-900 mb-0">{selectedPreauth.clinicalIndication}</p>
                                    </div>
                                }

                                {
                                    selectedPreauth?.rejectionReason &&
                                    <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3">
                                        <p className="text-xs font-semibold text-red-800 mb-1">Rejection Reason</p>
                                        <p className="text-sm text-red-900 mb-0">{selectedPreauth.rejectionReason}</p>
                                    </div>
                                }

                                {
                                    selectedPreauth?.accessPoint === 'IP' &&
                                    <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3">
                                        <p className="text-xs font-semibold text-amber-800 mb-1">Inpatient workflow</p>
                                        <p className="text-sm text-amber-900 mb-0">This preauthorization is scoped to inpatient care. Open the patient record and continue through the admission flow instead of starting an outpatient visit from this queue.</p>
                                    </div>
                                }

                                <div className="flex flex-wrap justify-end gap-2">
                                    {
                                        selectedPreauth?.patient &&
                                        <Link to={ `/app/accounts/sales/patients/${selectedPreauth.patient}?preauth=${selectedPreauth._id}&tab=bills` }>
                                            <button className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                                Open Patient Context
                                            </button>
                                        </Link>
                                    }
                                    {
                                        canActionVisit(selectedPreauth) &&
                                        <button
                                            disabled={actioningPreauth === selectedPreauth?._id}
                                            onClick={ () => actionPreauthVisit(selectedPreauth) }
                                            className="btn bg-indigo-600 border border-indigo-600 text-white hover:bg-indigo-700">
                                            {actioningPreauth === selectedPreauth?._id ? 'Opening…' : actionVisitLabel(selectedPreauth)}
                                        </button>
                                    }
                                    <button
                                        disabled={refreshing}
                                        onClick={() => refreshPreauthStatus({ id: selectedPreauth?._id })}
                                        className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                        Refresh SHA Status
                                    </button>
                                    {
                                        selectedPreauth?.isElective &&
                                        <button
                                            disabled={resending}
                                            onClick={() => resendPreauthDoctorConsent({ id: selectedPreauth?._id })}
                                            className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                            Resend Doctor Consent
                                        </button>
                                    }
                                </div>

                                <div className="rounded-sm border border-gray-200 px-4 py-3">
                                    <p className="text-xs font-semibold text-gray-700 mb-2">Recent SHA activity</p>
                                    <div className="space-y-2">
                                        {
                                            (selectedPreauth?.responseLog ?? []).slice(-6).reverse().map((entry, index) =>
                                                <div key={ `${entry?.type}-${index}` } className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                                                    <p className="text-sm font-medium mb-0">{entry?.type ?? 'event'}</p>
                                                    <p className="text-xs text-gray-500 mb-0">
                                                        {entry?.at ? stringifyDate(entry.at) : '—'}
                                                        {entry?.shaStatus ? ` • ${entry.shaStatus}` : ''}
                                                        {entry?.message ? ` • ${entry.message}` : ''}
                                                    </p>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    </Receive>
                </Drawer>
            }

            <div className="sm:flex sm:justify-between sm:items-start mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 mb-1">SHA Preauthorizations</h1>
                    <p className="text-sm text-gray-500 mb-0">Track approvals, elective visit dates, and follow-up actions for SHA preauthorizations from one work surface.</p>
                </div>
                <div className="text-right">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Visible records</p>
                    <p className="text-2xl font-semibold mb-0">{count}</p>
                </div>
            </div>

            <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-4 mb-4">
                <div className="grid gap-3 md:grid-cols-5 sm:grid-cols-1">
                    <input
                        value={ string }
                        onChange={ e => setString(e.target.value) }
                        className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        type="text"
                        placeholder="Search patient / procedure / indication" />
                    <input
                        value={ referenceNumber }
                        onChange={ e => setReferenceNumber(e.target.value) }
                        className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        type="text"
                        placeholder="Reference number" />
                    <Select value={ selectedQueue } onChange={ setSelectedQueue } options={ queueOptions } />
                    <Select value={ selectedStatus } onChange={ setSelectedStatus } options={ statusOptions } />
                    <Select value={ selectedCategory } onChange={ setSelectedCategory } options={ categoryOptions } />
                </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4 sm:grid-cols-1 mb-4">
                <button
                    type="button"
                    onClick={ () => setSelectedQueue(queueOptions[0]) }
                    className={ `rounded-sm border px-4 py-3 text-left transition border-gray-200 bg-white text-gray-900 ${selectedQueue?.value === '' ? 'ring-2 ring-offset-1 ring-gray-400' : ''}` }>
                    <p className="text-xs uppercase font-semibold mb-1 text-gray-500">All preauthorizations</p>
                    <p className="text-2xl font-semibold mb-1">{summaryPreauths.length}</p>
                    <p className="text-xs mb-0 text-gray-500">Return to the full view across elective and same-visit SHA preauth work.</p>
                </button>
                {
                    queueSummary.map(item =>
                        <button
                            key={ item.value }
                            type="button"
                            onClick={ () => setSelectedQueue(item) }
                            className={ `rounded-sm border px-4 py-3 text-left transition ${queueSummaryStyles[item.value] ?? 'border-gray-200 bg-gray-50 text-gray-900'} ${selectedQueue?.value === item.value ? 'ring-2 ring-offset-1 ring-gray-400' : ''}` }>
                            <p className="text-xs uppercase font-semibold mb-1 opacity-80">{item.label}</p>
                            <p className="text-2xl font-semibold mb-1">{item.count}</p>
                            <p className="text-xs mb-0 opacity-70">
                                {item.value === 'awaiting-doctor-approval' && 'Elective cases still waiting for doctor action at SHA'}
                                {item.value === 'awaiting-visit-date' && 'SHA-finalised elective preauths scheduled for a future visit'}
                                {item.value === 'due-today' && 'SHA-finalised elective preauths expected to be actioned today'}
                                {item.value === 'overdue' && 'SHA-finalised elective preauths whose scheduled visit date has passed'}
                            </p>
                        </button>
                    )
                }
            </div>

            {
                activeFilters.length > 0 &&
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    {
                        activeFilters.map(filter =>
                            <span key={ filter } className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 border border-gray-200">
                                {filter}
                            </span>
                        )
                    }
                    <button
                        type="button"
                        onClick={ clearFilters }
                        className="text-xs font-medium text-indigo-700 hover:text-indigo-900">
                        Clear all filters
                    </button>
                </div>
            }

            <Receive loading={ loading } error={ error } progress>
                {
                    preauths.length > 0 ?
                        <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-4">
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2 text-left">Patient / Procedure</th>
                                            <th className="p-2 text-left">Category</th>
                                            <th className="p-2 text-left">Status</th>
                                            <th className="p-2 text-left">SHA Status</th>
                                            <th className="p-2 text-left">Visit Queue</th>
                                            <th className="p-2 text-left">Updated</th>
                                            <th className="p-2 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            preauths.map(preauth =>
                                                <tr key={ preauth?._id }>
                                                    <td className="p-2">
                                                        <p className="mb-1">{preauth?.patientData?.name ?? 'Unknown patient'}</p>
                                                        <p className="text-xs text-gray-500 mb-0">
                                                            {preauth?.procedureName}
                                                            {preauth?.referenceNumber ? ` • ${preauth.referenceNumber}` : ''}
                                                        </p>
                                                    </td>
                                                    <td className="p-2">
                                                        <p className="mb-0">{preauth?.category}</p>
                                                        <p className="text-xs text-gray-500 mb-0">{preauth?.isElective ? 'Elective' : 'Non-elective'}</p>
                                                    </td>
                                                    <td className="p-2">
                                                        <span className={ `inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${badgeStyles[preauth?.status] ?? badgeStyles.pending}` }>
                                                            {preauth?.status ?? 'pending'}
                                                        </span>
                                                    </td>
                                                    <td className="p-2">{preauth?.shaStatus ?? '—'}</td>
                                                    <td className="p-2">
                                                        <p className="mb-0">{visitDateLabel(preauth)}</p>
                                                        <p className="text-xs text-gray-500 mb-0">{preauth?.isElective ? 'Elective queue' : 'Same-visit preauth'} • {preauth?.accessPoint ?? 'OP'}</p>
                                                    </td>
                                                    <td className="p-2">{stringifyDate(preauth?.updatedOn ?? preauth?.date)}</td>
                                                    <td className="p-2">
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={ () => openPreauth(preauth) }
                                                                className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                                                Open Details
                                                            </button>
                                                            {
                                                                preauth?.patient &&
                                                                <Link to={ `/app/accounts/sales/patients/${preauth.patient}?preauth=${preauth._id}&tab=bills` }>
                                                                    <button className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                                                        Patient Context
                                                                    </button>
                                                                </Link>
                                                            }
                                                            {
                                                                canActionVisit(preauth) &&
                                                                <button
                                                                    disabled={actioningPreauth === preauth?._id}
                                                                    onClick={ () => actionPreauthVisit(preauth) }
                                                                    className="btn bg-indigo-600 border border-indigo-600 text-white hover:bg-indigo-700">
                                                                    {actioningPreauth === preauth?._id ? 'Opening…' : actionVisitLabel(preauth)}
                                                                </button>
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            {Paginate}
                        </div>
                        :
                        <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-8">
                            <p className="text-sm text-gray-700 mb-2">No SHA preauthorizations matched the current filters.</p>
                            <p className="text-sm text-gray-500 mb-0">
                                {selectedQueue?.value === 'due-today' && 'There are no elective SHA preauthorizations scheduled for today under the current filters.'}
                                {selectedQueue?.value === 'overdue' && 'There are no overdue elective SHA preauthorizations under the current filters.'}
                                {selectedQueue?.value === 'awaiting-doctor-approval' && 'There are no elective SHA preauthorizations still waiting for doctor approval under the current filters.'}
                                {selectedQueue?.value === 'awaiting-visit-date' && 'There are no SHA-finalised elective preauthorizations waiting for a future visit date under the current filters.'}
                                {!selectedQueue?.value && 'Try clearing one or more filters to broaden the worklist.'}
                            </p>
                            <p className="text-xs text-gray-500 mt-3 mb-0">
                                If you are trying to bill a same-visit service right now, use the inline preauthorization interruption flow from billing instead of this queue.
                            </p>
                        </div>
                }
            </Receive>
        </div>
    )
}

export default ShaPreauths
